import { useEffect, useState } from 'react';

import { apiClient } from '@/services';
import { cn } from '@/services/utils';

export type TrainingMode = 'en-ru' | 'ru-en' | 'definition-word';

export const isTrainingMode = (value: unknown): value is TrainingMode =>
  value === 'en-ru' || value === 'ru-en' || value === 'definition-word';

type TrainingModeOption = {
  value: TrainingMode;
  title: string;
  description: string;
  image: string;
  imageWidth: number;
  imageHeight: number;
};

export const trainingModes: TrainingModeOption[] = [
  {
    value: 'en-ru',
    title: 'Phrase first',
    description: 'You see the phrase — say what it means',
    image: '/training-modes/phrase-first.png',
    imageWidth: 620,
    imageHeight: 413,
  },
  {
    value: 'ru-en',
    title: 'Meaning first',
    description: 'You see the translation — say the phrase',
    image: '/training-modes/meaning-first.png',
    imageWidth: 620,
    imageHeight: 465,
  },
  {
    value: 'definition-word',
    title: 'Definition first',
    description: 'You read the English definition — name the phrase',
    image: '/training-modes/definition-first.png',
    imageWidth: 620,
    imageHeight: 465,
  },
];

// Remembers the mode across visits so the picker opens with last time's choice already
// highlighted (design bundle: "18a" — "second time through is a single Enter/click").
const LAST_MODE_STORAGE_KEY = 'yawca:training:lastMode';

const readStoredMode = (): TrainingMode | null => {
  try {
    const stored = window.localStorage.getItem(LAST_MODE_STORAGE_KEY);
    return isTrainingMode(stored) ? stored : null;
  } catch {
    return null;
  }
};

const storeMode = (mode: TrainingMode) => {
  try {
    window.localStorage.setItem(LAST_MODE_STORAGE_KEY, mode);
  } catch {
    // Private-browsing / storage-blocked — the picker still works, it just won't remember.
  }
};

type SidebarTag = { id: string; name: string; cardCount: number };

// Sized to match /meaning's ThisWeekWidget (same widget slot, same panel): text-lg title,
// text-[36px] display-font stat, text-[15px] stat label, text-[14.5px] secondary line.
function DueNowCard({ dueCount, loading }: { dueCount: number; loading: boolean }) {
  return (
    <div className="flex-none rounded-2xl border border-[#ece9e2] bg-white px-[18px] py-4">
      <div className="text-lg font-extrabold text-[#1e1c18]">Due now</div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-[family-name:var(--font-display)] text-[36px] font-semibold leading-none text-[#1e1c18]">
          {loading ? '–' : dueCount}
        </span>
        <span className="text-[15px] text-[#57534a]">cards ready</span>
      </div>
      <div className="mt-1 text-[14.5px] leading-[1.4] text-[#a39c8d]">
        Practise as many as you like — nothing has to be finished.
      </div>
    </div>
  );
}

// Read-only tag list for narrowing the deck before training starts. Selecting a tag only
// highlights it for now: the training endpoint has no tag filter yet, so this is UI-only
// until the backend can hand back a tag-scoped deck (design bundle: "18a" side note
// "здесь же сужение колоды"). Sized to match /meaning's TagsSidebarList (same panel shape):
// text-lg title, text-[13px] count, text-[15px] rows — only the extra subtitle line below the
// title has no TagsSidebarList equivalent to match against.
function PractiseTagCard({
  tags,
  loading,
  error,
  totalTagCount,
  totalCardCount,
  selectedTagId,
  onSelect,
}: {
  tags: SidebarTag[];
  loading: boolean;
  error: string;
  totalTagCount: number;
  totalCardCount: number;
  selectedTagId: string | null;
  onSelect: (tagId: string | null) => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col rounded-2xl border border-[#ece9e2] bg-white px-3 pb-3 pt-3.5">
      <div className="px-[5px] pb-2.5">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-lg font-extrabold text-[#1e1c18]">Practise a tag</div>
          <div className="text-[13px] text-[#a39c8d]">{loading ? '…' : totalTagCount}</div>
        </div>
        <div className="mt-0.5 text-[12.5px] leading-[1.4] text-[#a39c8d]">
          Or narrow the deck before you start
        </div>
      </div>
      <div className="flex min-h-0 flex-col gap-px overflow-y-auto">
        {error && (
          <div role="alert" className="px-[11px] py-2 text-xs font-medium text-red-700">
            {error}
          </div>
        )}
        <button
          type="button"
          onClick={() => onSelect(null)}
          className={cn(
            'flex items-center justify-between gap-2 rounded-[9px] px-[11px] py-[7px] text-left text-[15px] font-semibold text-[#3d3a33] hover:bg-[#f6f4ef]',
            selectedTagId === null && 'bg-[#2f5c4e] font-bold text-white hover:bg-[#2f5c4e]'
          )}
        >
          <span className="min-w-0 truncate">All words</span>
          <span className={cn('shrink-0 font-semibold text-[#a39c8d]', selectedTagId === null && 'text-white/75')}>
            {loading ? '…' : totalCardCount}
          </span>
        </button>
        {tags.map((tag) => {
          const selected = selectedTagId === tag.id;
          return (
            <button
              key={tag.id}
              type="button"
              onClick={() => onSelect(selected ? null : tag.id)}
              className={cn(
                'flex items-center justify-between gap-2 rounded-[9px] px-[11px] py-[7px] text-left text-[15px] font-semibold text-[#3d3a33] hover:bg-[#f6f4ef]',
                selected && 'bg-[#2f5c4e] font-bold text-white hover:bg-[#2f5c4e]'
              )}
            >
              <span className="min-w-0 truncate">{tag.name}</span>
              <span className={cn('shrink-0 font-semibold text-[#a39c8d]', selected && 'text-white/75')}>
                {tag.cardCount}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function TrainingModePicker({
  dueCount,
  loadingCards,
  onSelect,
}: {
  dueCount: number;
  loadingCards: boolean;
  onSelect: (mode: TrainingMode) => void;
}) {
  const [highlightedMode, setHighlightedMode] = useState<TrainingMode>(() => readStoredMode() ?? 'en-ru');
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [tags, setTags] = useState<SidebarTag[]>([]);
  const [totalTagCount, setTotalTagCount] = useState(0);
  const [totalCardCount, setTotalCardCount] = useState(0);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [tagsError, setTagsError] = useState('');

  useEffect(() => {
    let mounted = true;

    apiClient.getTagStatistics()
      .then((statistics) => {
        if (!mounted) return;
        setTags(
          (statistics.tags ?? [])
            .map((tag) => ({ id: tag.id ?? '', name: tag.name ?? '', cardCount: tag.cardCount ?? 0 }))
            .filter((tag) => tag.id && tag.name)
        );
        setTotalTagCount(statistics.totalTagCount ?? 0);
        setTotalCardCount(statistics.totalCardCount ?? 0);
      })
      .catch(() => {
        if (mounted) setTagsError('Unable to load tags.');
      })
      .finally(() => {
        if (mounted) setTagsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const chooseMode = (mode: TrainingMode) => {
    setHighlightedMode(mode);
    storeMode(mode);
  };

  const startTraining = () => onSelect(highlightedMode);

  const sidebar = (
    <>
      <DueNowCard dueCount={dueCount} loading={loadingCards} />
      <PractiseTagCard
        tags={tags}
        loading={tagsLoading}
        error={tagsError}
        totalTagCount={totalTagCount}
        totalCardCount={totalCardCount}
        selectedTagId={selectedTagId}
        onSelect={setSelectedTagId}
      />
    </>
  );

  const main = (
    <>
      <header className="max-w-xl">
        <h1 className="font-[family-name:var(--font-display)] text-[1.9rem] font-semibold leading-[1.15] tracking-[-0.01em] text-[#1e1c18]">
          How should we ask you?
        </h1>
        <p className="mt-2 text-sm leading-[1.55] text-[#6b6659]">
          Same {loadingCards ? '…' : dueCount} cards either way — only the side you see first changes. Pick
          once; you can change it next time you come in.
        </p>
      </header>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-stretch">
        {trainingModes.map((mode) => {
          const selected = highlightedMode === mode.value;
          return (
            <button
              key={mode.value}
              type="button"
              onClick={() => chooseMode(mode.value)}
              aria-pressed={selected}
              aria-label={`Select ${mode.title} training mode`}
              className={cn(
                'flex min-w-0 flex-1 flex-col gap-3.5 rounded-2xl border bg-white p-4 text-left transition sm:p-5',
                selected
                  ? 'border-[#2f5c4e] shadow-[0_0_0_3px_rgba(47,92,78,0.12)]'
                  : 'border-[#e6e2da] hover:border-[#d9e2d9]'
              )}
            >
              <span className="flex-none overflow-hidden rounded-xl bg-[#faf8f4]">
                <img
                  src={mode.image}
                  alt=""
                  aria-hidden="true"
                  width={mode.imageWidth}
                  height={mode.imageHeight}
                  className="block h-36 w-full object-cover object-center sm:h-40"
                />
              </span>
              <span className="min-w-0">
                <span className="block font-[family-name:var(--font-display)] text-[21px] font-semibold text-[#1e1c18]">
                  {mode.title}
                </span>
                <span className="mt-2 block text-[14px] leading-[1.5] text-[#57534a]">
                  {mode.description}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3.5">
        <button
          type="button"
          onClick={startTraining}
          disabled={loadingCards || dueCount === 0}
          className="flex h-[42px] items-center whitespace-nowrap rounded-full bg-[#0f1115] px-[22px] text-sm font-bold text-white transition hover:bg-[#1e1c18] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Practise {loadingCards ? '…' : dueCount} cards
        </button>
        <span className="text-[12.5px] text-[#a39c8d]">Enter — start · 1 / 2 / 3 — pick a mode</span>
      </div>
    </>
  );

  return (
    <section
      aria-label="How should we ask you?"
      className="w-full flex-1 pb-8 pt-4 font-[family-name:var(--font-meaning-sans)] sm:pb-10 sm:pt-6 md:pt-0"
    >
      {/* Mobile: simple stack. Desktop: sidebar keeps the same 348px column as /meaning's aside
          (so it lines up under the header's logo), but the main column fills the rest of the
          1364px frame instead of being capped at meaning's 668px feed width — screen 18a's own
          mode cards are meant to be much bigger than a feed card. Main sits a bit lower than the
          sidebar (matching the design's own main column, which centres its content vertically),
          but only by a fixed offset — centring in the full viewport made the gap balloon on tall
          screens. */}
      <div className="flex flex-col gap-6 md:hidden">
        <div className="flex flex-col gap-3.5">{sidebar}</div>
        <div>{main}</div>
      </div>

      <div className="hidden md:mx-auto md:grid md:max-w-[1364px] md:grid-cols-[348px_1fr] md:items-start md:gap-0">
        <div className="flex flex-col gap-3.5 p-4 pt-8">{sidebar}</div>
        <div className="min-w-0 px-9 pb-10 pt-12">{main}</div>
      </div>
    </section>
  );
}
